import * as FileSaver from 'file-saver'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAuthenticatedRequestor } from '@app/rest/rest.hooks'
import { getDocumentFileName } from '@app/state/visits/rest'

import {
  getInteraction,
  getInteractionCommunicationConsent,
  getInteractionReportTemplates,
  requestInteractionPDF,
} from './interaction.rest'

import type { IEntry } from '../instrument/instrument.interface'
import type { IPatientId } from '../patient/patient.interface'
import type {
  IDataInstrument,
  InteractionCommunicationConsent,
  PdfTemplate,
} from '../visit/visit.interface'


export type IUseInteractionFetchStatus =
  | 'idle'
  | 'loading'
  | 'error'
  | 'success'
export type IUseInteractionStatus = {
  fetchInteraction: IUseInteractionFetchStatus
  fetchCommunicationConsent: IUseInteractionFetchStatus
  fetchReportTemplates: IUseInteractionFetchStatus
  overall: IUseInteractionFetchStatus
}

export interface IUseInteraction {
  patientId: IPatientId
  visitId: string
  entries: IEntry[]
  instruments: IDataInstrument[]
  communicationConsent?: InteractionCommunicationConsent
  reportTemplates: PdfTemplate[]
  status: IUseInteractionStatus
}

export function useInteraction(interactionId?: string): IUseInteraction {
  const requestor = useAuthenticatedRequestor()
  const [patientId, setPatientId] = useState<IPatientId>()
  const [visitId, setVisitId] = useState<string>()
  const [status, setStatus] = useState<IUseInteractionStatus>({
    fetchInteraction: 'idle',
    fetchCommunicationConsent: 'idle',
    fetchReportTemplates: 'idle',
    overall: 'idle',
  })

  const [entries, setEntries] = useState<IEntry[]>([])
  const [instruments, setInstruments] = useState<IDataInstrument[]>([])
  const [reportTemplates, setReportTemplates] = useState<PdfTemplate[]>([])

  const [communicationConsent, setCommunicationConsent] = useState<
    InteractionCommunicationConsent | undefined
  >(undefined)

  const fetchInteraction = useCallback(async () => {
    setStatus((s) => ({ ...s, fetchInteraction: 'loading' }))
    const response = await requestor(getInteraction(interactionId))

    if (response.status === 200) {
      setPatientId(response.data.patientId)
      setVisitId(response.data.visitId)
      setEntries(response.data.entries)
      setInstruments(response.data.instruments)
      setStatus((s) => ({ ...s, fetchInteraction: 'success' }))
    } else {
      setStatus((s) => ({ ...s, fetchInteraction: 'error' }))
      throw new Error(`server returned a non 200 response '${response.status}'`)
    }
  }, [interactionId, requestor])

  const fetchCommunicationConsent = useCallback(async () => {
    setStatus((s) => ({ ...s, fetchCommunicationConsent: 'loading' }))
    const response = await requestor(
      getInteractionCommunicationConsent(interactionId),
    )

    if (response.status === 200) {
      setCommunicationConsent({
        didConsent: response.data.didConsent,
        updatedAt: new Date(response.data.updatedAt),
      })
      setStatus((s) => ({ ...s, fetchCommunicationConsent: 'success' }))
    } else if (response.status === 404) {
      setCommunicationConsent(undefined)
      setStatus((s) => ({ ...s, fetchCommunicationConsent: 'success' }))
    } else {
      setStatus((s) => ({ ...s, fetchCommunicationConsent: 'error' }))
      throw new Error(`server returned a non 200 response '${response.status}'`)
    }
  }, [interactionId, requestor])

  const fetchReportTemplates = useCallback(async () => {
    setStatus((s) => ({ ...s, fetchReportTemplates: 'loading' }))
    const response = await requestor(
      getInteractionReportTemplates(interactionId),
    )

    if (response.status === 200) {
      setReportTemplates(response.data.reportTemplates)
      setStatus((s) => ({ ...s, fetchReportTemplates: 'success' }))
    } else {
      setStatus((s) => ({ ...s, fetchReportTemplates: 'error' }))
      throw new Error(`server returned a non 200 response '${response.status}'`)
    }
  }, [interactionId, requestor])

  useEffect(() => {
    if (interactionId) {
      fetchInteraction()
      fetchCommunicationConsent()
      fetchReportTemplates()
    }
  }, [
    fetchInteraction,
    fetchCommunicationConsent,
    fetchReportTemplates,
    interactionId,
  ])

  const overallStatus = useMemo(() => {
    const { overall, ...statuses } = status
    if (Object.values(statuses).every((s) => s === 'success')) {
      return 'success'
    } else if (Object.values(statuses).some((s) => s === 'loading')) {
      return 'loading'
    } else if (Object.values(statuses).some((s) => s === 'error')) {
      return 'error'
    } else {
      return 'idle'
    }
  }, [status])

  return {
    patientId,
    visitId,
    entries,
    instruments,
    communicationConsent,
    reportTemplates,
    status: {
      ...status,
      overall: overallStatus,
    },
  }
}

export interface IUseGetInteractionPdf {
  getInteractionPdf: (interactionId: string, templateId: string) => void
  interactionPdfError: string | null
  clearInteractionPdfError: () => void
  isInteractionPdfLoading: boolean
}

export function useGetInteractionPdf(): IUseGetInteractionPdf {
  const requestor = useAuthenticatedRequestor()
  const [interactionPdfError, setInteractionPdfError] = useState<string | null>(
    null,
  )
  const [isInteractionPdfLoading, setIsInteractionPdfLoading] = useState(false)

  const fetchInteractionPdf = useCallback(
    async (interactionId: string, templateId: string) => {
      setInteractionPdfError(null)
      setIsInteractionPdfLoading(true)
      try {
        const response = await requestor(
          requestInteractionPDF(interactionId, templateId),
        )

        if (response.status === 200) {
          const fileName = getDocumentFileName(
            response.headers['content-disposition'],
            'patient-entry',
            'pdf',
          )
          FileSaver.saveAs(response.data, fileName)
        } else {
          throw new Error(
            `server returned a non 200 response '${response.status}'`,
          )
        }
      } catch (e) {
        setInteractionPdfError('Failed to download interaction report.')
      } finally {
        setIsInteractionPdfLoading(false)
      }
    },
    [requestor],
  )

  const clearInteractionPdfError = useCallback(() => {
    setInteractionPdfError(null)
  }, [])

  return {
    getInteractionPdf: fetchInteractionPdf,
    interactionPdfError,
    clearInteractionPdfError,
    isInteractionPdfLoading,
  }
}
